import {
  isNil,
  isArray,
  isBoolean,
  isEmpty,
  isNaN,
  isNull,
  isNumber,
  isString,
  isUndefined,
  isEqual,
  omit,
} from 'lodash';

import {
  PATTERN_FIELD_TYPES,
  PATTERN_ARRAY_OPERATORS,
  PATTERN_BOOLEAN_OPERATORS,
  PATTERN_DURATION_OPERATORS,
  PATTERN_EXISTS_OPERATORS,
  PATTERN_NULL_OPERATORS,
  PATTERN_NUMBER_OPERATORS,
  PATTERN_OPERATORS_WITHOUT_VALUE,
  PATTERN_RULE_INFOS_FIELDS,
  PATTERN_RULE_TYPES,
  PATTERN_STRING_OPERATORS,
  PATTERN_CONDITIONS,
  PATTERN_DATE_OPERATORS,
  ALARM_PATTERN_FIELDS,
  ENTITY_PATTERN_FIELDS,
  EVENT_FILTER_PATTERN_FIELDS,
  PATTERN_OPERATORS,
  SERVICE_WEATHER_PATTERN_FIELDS,
  QUICK_RANGES,
  PATTERNS_FIELDS,
  PATTERN_CUSTOM_ITEM_VALUE,
  PATTERN_TYPES,
  PATTERN_QUICK_RANGES_DURATIONS,
  DYNAMIC_INFO_FIELDS,
  PBEHAVIOR_FIELDS,
  PBEHAVIOR_PATTERN_FIELDS,
} from '@/constants';

import { uid } from '@/helpers/uid';
import { convertDateToDateObject, convertDateToTimestamp, isValidDateInterval } from '@/helpers/date/date';
import { durationToForm, isValidDuration, isValidRangeDuration } from '@/helpers/date/duration';
import { primitiveArrayToForm, formToPrimitiveArray } from '@/helpers/entities/shared/form';

/**
 * @typedef { 'string' | 'number' | 'infos' | 'date' | 'duration' } PatternRuleType
 */

/**
 * @typedef { 'string' | 'int' | 'bool' | 'null' | 'string_array' } PatternFieldType
 */

/**
 * @typedef {boolean | [] | null | string | number} PatternValue
 */

/**
 * @typedef { 'alarm' | 'entity' | 'pbehavior' } PatternTypes
 */

/**
 * @typedef { 'alarm_pattern' | 'entity_pattern' | 'pbehavior_pattern' | 'total_entity_pattern' } PatternsField
 */

/**
 * @typedef {PatternsField[]} PatternsFields
 */

/**
 * @typedef {Object} PatternRuleRangeCondition
 * @property {number} from
 * @property {number} to
 */

/**
 * @typedef {Object} PatternRuleCondition
 * @property {string} type
 * @property {PatternRuleRangeCondition | Duration | string | number | boolean | []} value
 */

/**
 * @typedef {Object} PatternRule
 * @property {PatternRuleCondition} cond
 * @property {string} field
 * @property {string} [field_type]
 * @property {boolean} [alias]
 */

/**
 * @typedef {PatternRule[]} PatternRules
 */

/**
 * @typedef {PatternRules[]} PatternGroups
 */

/**
 * @typedef {Object} PatternRuleRangeForm
 * @property {string} type
 * @property {string | number} [from]
 * @property {string | number} [to]
 */

/**
 * @typedef {Object & ObjectKey} PatternRuleForm
 * @property {string} attribute
 * @property {string} operator
 * @property {string} field
 * @property {string} fieldType
 * @property {string} definedType
 * @property {string} dictionary
 * @property {number | string} value
 * @property {PatternRuleRangeForm} range
 * @property {Duration} duration
 * @property {boolean} alias
 */

/**
 * @typedef {Object & ObjectKey} PatternGroupForm
 * @property {PatternRuleForm[]} rules
 */

/**
 * @typedef {PatternRuleForm[]} PatternGroupsForm
 */

/**
 * @typedef {Pattern} PatternForm
 * @property {PatternGroupsForm} groups
 */

/**
 * Check is operator has a value
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isOperatorHasValue = operator => !PATTERN_OPERATORS_WITHOUT_VALUE.includes(operator);

/**
 * Check is operator for array
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isOperatorForArray = operator => PATTERN_ARRAY_OPERATORS.includes(operator);

/**
 * Check is operator for string
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isOperatorForString = operator => PATTERN_STRING_OPERATORS.includes(operator);

/**
 * Check if operator is date interval type (within, olderThan)
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isIntervalDateOperator = operator => [
  PATTERN_OPERATORS.within,
  PATTERN_OPERATORS.olderThan,
].includes(operator);

/**
 * Check is operator for number
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isOperatorForNumber = operator => PATTERN_NUMBER_OPERATORS.includes(operator);

/**
 * Check is operator for boolean
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isOperatorForBoolean = operator => PATTERN_BOOLEAN_OPERATORS.includes(operator);

/**
 * Check is operator for null
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isOperatorForNull = operator => PATTERN_NULL_OPERATORS.includes(operator);

/**
 * Check rule is infos
 *
 * @param {string} type
 * @return {boolean}
 */
export const isInfosRuleType = type => type === PATTERN_RULE_TYPES.infos;

/**
 * Check rule is extra infos
 *
 * @param {string} type
 * @return {boolean}
 */
export const isExtraInfosRuleType = type => type === PATTERN_RULE_TYPES.extraInfos;

/**
 * Check rule is date
 *
 * @param {string} type
 * @return {boolean}
 */
export const isDateRuleType = type => type === PATTERN_RULE_TYPES.date;

/**
 * Check rule is duration
 *
 * @param {string} type
 * @return {boolean}
 */
export const isDurationRuleType = type => type === PATTERN_RULE_TYPES.duration;

/**
 * Check rule is object
 *
 * @param {string} type
 * @return {boolean}
 */
export const isObjectRuleType = type => type === PATTERN_RULE_TYPES.object;

/**
 * Check rule is number
 *
 * @param {string} type
 * @return {boolean}
 */
export const isNumberRuleType = type => type === PATTERN_RULE_TYPES.number;

/**
 * Check field type is string array
 *
 * @param {PatternFieldType} type
 * @return {boolean}
 */
export const isStringArrayFieldType = type => type === PATTERN_FIELD_TYPES.stringArray;

/**
 * Check field type is valid
 *
 * @param {*} value
 * @return {boolean}
 */
export const isValidRuleFieldType = value => Object.values(PATTERN_FIELD_TYPES).includes(value);

/**
 * Check condition is boolean
 *
 * @param {string} condition
 * @return {boolean}
 */
export const isBooleanCondition = condition => [
  PATTERN_CONDITIONS.equal,
  PATTERN_CONDITIONS.notEqual,
  PATTERN_CONDITIONS.exist,
  PATTERN_CONDITIONS.isEmpty,
].includes(condition);

/**
 * Check condition is boolean
 *
 * @todo: remove this function in the future and use isArrayOperator instead
 *
 * @param {string} condition
 * @return {boolean}
 */
export const isArrayCondition = condition => [
  PATTERN_CONDITIONS.isEmpty,
  PATTERN_CONDITIONS.hasNot,
  PATTERN_CONDITIONS.hasOneOf,
  PATTERN_CONDITIONS.isOneOf,
  PATTERN_CONDITIONS.isNotOneOf,
  PATTERN_CONDITIONS.hasEvery,
  PATTERN_CONDITIONS.hasLabels,
  PATTERN_CONDITIONS.hasNotLabels,
].includes(condition);

/**
 * Check operator is array
 *
 * @param {string} operator
 * @return {boolean}
 */
export const isArrayOperator = operator => PATTERN_ARRAY_OPERATORS.includes(operator);

/**
 * Check condition is valid
 *
 * @param {string} condition
 * @return {boolean}
 */
export const isValidPatternCondition = condition => Object.values(PATTERN_CONDITIONS).includes(condition);

/**
 * Check pattern field is date
 *
 * @param {string} value
 * @return {boolean}
 */
export const isDatePatternRuleField = value => [
  ALARM_PATTERN_FIELDS.creationDate,
  ALARM_PATTERN_FIELDS.lastEventDate,
  ALARM_PATTERN_FIELDS.lastUpdateDate,
  ALARM_PATTERN_FIELDS.ackAt,
  ALARM_PATTERN_FIELDS.resolved,
  ALARM_PATTERN_FIELDS.activationDate,
  ENTITY_PATTERN_FIELDS.idleSince,
  ENTITY_PATTERN_FIELDS.imported,
  ENTITY_PATTERN_FIELDS.lastUpdateDate,
  ENTITY_PATTERN_FIELDS.lastAlarmUpdateDate,
  ENTITY_PATTERN_FIELDS.lastPbehaviorDate,
  ENTITY_PATTERN_FIELDS.lastEventDate,
  ENTITY_PATTERN_FIELDS.lastEventDate,
  DYNAMIC_INFO_FIELDS.created,
  DYNAMIC_INFO_FIELDS.updated,

  /**
   * Fields for advanced search
   */
  PBEHAVIOR_FIELDS.tstart,
  PBEHAVIOR_FIELDS.tstop,
  PBEHAVIOR_FIELDS.rruleEnd,
  PBEHAVIOR_FIELDS.created,
  PBEHAVIOR_FIELDS.updated,
  PBEHAVIOR_FIELDS.lastAlarmDate,
].includes(value);

/**
 * Check pattern field is number
 *
 * @param {string} value
 * @return {boolean}
 */
export const isNumberPatternRuleField = value => [
  ALARM_PATTERN_FIELDS.state,
  ALARM_PATTERN_FIELDS.status,
  ALARM_PATTERN_FIELDS.totalStateChanges,
  EVENT_FILTER_PATTERN_FIELDS.state,
  ENTITY_PATTERN_FIELDS.impactLevel,
  SERVICE_WEATHER_PATTERN_FIELDS.state,

  /**
   * Fields for advanced search
   */
  PBEHAVIOR_FIELDS.alarmCount,
].includes(value);

/**
 * Check pattern field is array
 *
 * @param {string} value
 * @return {boolean}
 */
export const isArrayPatternRuleField = value => [
  ALARM_PATTERN_FIELDS.displayName,
  ALARM_PATTERN_FIELDS.output,
  ALARM_PATTERN_FIELDS.longOutput,
  ALARM_PATTERN_FIELDS.initialOutput,
  ALARM_PATTERN_FIELDS.initialLongOutput,
  ALARM_PATTERN_FIELDS.component,
  ALARM_PATTERN_FIELDS.connector,
  ALARM_PATTERN_FIELDS.connectorName,
  ALARM_PATTERN_FIELDS.resource,
  ALARM_PATTERN_FIELDS.tags,
  ALARM_PATTERN_FIELDS.lastComment,
  ALARM_PATTERN_FIELDS.lastCommentInitiator,
  ALARM_PATTERN_FIELDS.lastCommentAuthor,
  ALARM_PATTERN_FIELDS.ticketMessage,
  ALARM_PATTERN_FIELDS.ticketValue,
  ALARM_PATTERN_FIELDS.ticketInitiator,
  ALARM_PATTERN_FIELDS.ticketData,
  ALARM_PATTERN_FIELDS.ackBy,
  ALARM_PATTERN_FIELDS.ackMessage,
  ALARM_PATTERN_FIELDS.ackInitiator,
  ALARM_PATTERN_FIELDS.canceledInitiator,
  ALARM_PATTERN_FIELDS.stateInitiator,
  ENTITY_PATTERN_FIELDS.id,
  ENTITY_PATTERN_FIELDS.name,
  ENTITY_PATTERN_FIELDS.category,
  ENTITY_PATTERN_FIELDS.type,
  ENTITY_PATTERN_FIELDS.connector,
  ENTITY_PATTERN_FIELDS.component,
  EVENT_FILTER_PATTERN_FIELDS.component,
  EVENT_FILTER_PATTERN_FIELDS.connector,
  EVENT_FILTER_PATTERN_FIELDS.connectorName,
  EVENT_FILTER_PATTERN_FIELDS.resource,
  EVENT_FILTER_PATTERN_FIELDS.output,
  EVENT_FILTER_PATTERN_FIELDS.longOutput,
  EVENT_FILTER_PATTERN_FIELDS.eventType,
  EVENT_FILTER_PATTERN_FIELDS.sourceType,
  EVENT_FILTER_PATTERN_FIELDS.initiator,
  EVENT_FILTER_PATTERN_FIELDS.author,
  PBEHAVIOR_PATTERN_FIELDS.name,

  /**
   * Fields for advanced search
   */
  PBEHAVIOR_FIELDS.name,
  PBEHAVIOR_FIELDS.author,
  PBEHAVIOR_FIELDS.rrule,
  PBEHAVIOR_FIELDS.reason,
  PBEHAVIOR_FIELDS.type,
  PBEHAVIOR_FIELDS.canonicalType,
].some((field) => {
  /**
   * @TODO: update babel-eslint for resolving problem with templates inside optional chaiging function call
   */
  const start = `${field}.`;

  return value === field || value?.startsWith(start);
});

/**
 * Check pattern field is infos
 *
 * @param {string} value
 * @return {boolean}
 */
export const isInfosPatternRuleField = value => [
  ALARM_PATTERN_FIELDS.infos,
  ALARM_PATTERN_FIELDS.entityInfos,
  ALARM_PATTERN_FIELDS.entityComponentInfos,
  ENTITY_PATTERN_FIELDS.componentInfos,
  ENTITY_PATTERN_FIELDS.infos,
].some((field) => {
  /**
   * @TODO: update babel-eslint for resolving problem with templates inside optional chaiging function call
   */
  const start = `${field}.`;

  return value === field || value?.startsWith?.(start);
});

/**
 * Check pattern field is infos value
 *
 * @param {string} [value = '']
 * @return {boolean}
 */
export const isValueInfosPatternRuleField = value => isInfosPatternRuleField(value) && value?.endsWith?.('.value');

/**
 * Check pattern field is duration
 *
 * @param {string} value
 * @return {boolean}
 */
export const isDurationPatternRuleField = value => value === ALARM_PATTERN_FIELDS.duration;

/**
 * Check pattern is extra infos
 *
 * @param {string} value
 * @return {boolean}
 */
export const isExtraInfosPatternRuleField = (value) => {
  /**
   * @TODO: update babel-eslint for resolving problem with templates inside optional chaining function call
   */
  const start = `${EVENT_FILTER_PATTERN_FIELDS.extraInfos}.`;

  return value === EVENT_FILTER_PATTERN_FIELDS.extraInfos
    || value?.startsWith(start);
};

/**
 * Get object pattern field
 *
 * @param {string} value
 * @return {string}
 */
export const getObjectPatternRuleField = value => [ALARM_PATTERN_FIELDS.ticketData]
  .find((field) => {
    /**
     * @TODO: update babel-eslint for resolving problem with templates inside optional chaining function call
     */
    const start = `${field}.`;

    return value === field || value?.startsWith(start);
  });

/**
 * Check pattern field is object
 *
 * @param {string} value
 * @return {boolean}
 */
export const isObjectPatternRuleField = value => !!getObjectPatternRuleField(value);

/**
 * Check rule value is valid without field type
 *
 * @param {PatternRule | *} rule
 * @return {boolean}
 */
export const isValidRuleValueWithoutFieldType = (rule) => {
  const { field, cond } = rule;

  if (isDatePatternRuleField(field)) {
    if (cond.type === PATTERN_CONDITIONS.absoluteTime) {
      return isValidDateInterval(cond?.value);
    }

    if (cond.type === PATTERN_CONDITIONS.relativeTime) {
      return isValidRangeDuration(cond.value);
    }
  }

  if (isDurationPatternRuleField(field)) {
    return isValidDuration(cond?.value);
  }

  if (isNumberPatternRuleField(field)) {
    return isNumber(cond.value);
  }

  if (isArrayPatternRuleField(field)) {
    if (isArrayCondition(cond.type)) {
      return isArray(cond.value) || (isBoolean(cond.value) && cond.type === PATTERN_CONDITIONS.isEmpty);
    }
  }

  if (isBoolean(cond.value)) {
    return isBooleanCondition(cond.type);
  }

  return isString(cond.value);
};

/**
 * Return field type by value
 *
 * @param {PatternValue} value
 * @return {string}
 */
export const getFieldType = (value) => {
  if (isBoolean(value)) {
    return PATTERN_FIELD_TYPES.boolean;
  }

  if (isNumber(value)) {
    return PATTERN_FIELD_TYPES.number;
  }

  if (isNull(value)) {
    return PATTERN_FIELD_TYPES.null;
  }

  if (isArray(value)) {
    return PATTERN_FIELD_TYPES.stringArray;
  }

  return PATTERN_FIELD_TYPES.string;
};

/**
 * Check rule value is valid with field type
 *
 * @param {PatternRule | *} rule
 * @return {boolean}
 */
export const isValidRuleValueWithFieldType = (rule) => {
  const { field, alias, cond, field_type: fieldType } = rule;

  if ([PATTERN_FIELD_TYPES.stringArray, PATTERN_FIELD_TYPES.string].includes(fieldType)) {
    if (isArrayCondition(cond.type)) {
      return (isArray(cond.value) && cond.value.every(isString))
        || (isBoolean(cond.value) && cond.type === PATTERN_CONDITIONS.isEmpty);
    }

    if (fieldType === PATTERN_FIELD_TYPES.stringArray) {
      return false;
    }
  }

  const isInfos = isInfosPatternRuleField(field) || isExtraInfosPatternRuleField(field);

  return (isInfos || alias) && getFieldType(cond.value) === fieldType;
};

/**
 * Check rule value is valid
 *
 * @param {PatternRule | *} rule
 * @return {boolean}
 */
export const isValidRuleValue = rule => (
  rule.field_type
    ? isValidRuleValueWithFieldType(rule)
    : isValidRuleValueWithoutFieldType(rule)
);

/**
 * Whether a pattern rule field uses a generic string-array editor, as opposed to fields that bind
 * primitives or dedicated pickers (tags, context entities, event filter entities, user/initiator fields, etc.).
 *
 * @param {string} field Rule field id (alarm, entity, or event-filter pattern field constant).
 * @return {boolean} True when the field is not one of the excluded primitive / specialized array fields.
 */
export const isArrayPatternRuleFieldWithoutPrimitive = field => (
  field !== ALARM_PATTERN_FIELDS.tags
    && field !== ALARM_PATTERN_FIELDS.component
    && field !== ALARM_PATTERN_FIELDS.connector
    && field !== ALARM_PATTERN_FIELDS.connectorName
    && field !== ALARM_PATTERN_FIELDS.resource
    && field !== ALARM_PATTERN_FIELDS.ackBy
    && field !== ALARM_PATTERN_FIELDS.ackInitiator
    && field !== ALARM_PATTERN_FIELDS.ticketInitiator
    && field !== ALARM_PATTERN_FIELDS.snoozeAuthor
    && field !== ALARM_PATTERN_FIELDS.snoozeInitiator
    && field !== ALARM_PATTERN_FIELDS.canceledInitiator
    && field !== ALARM_PATTERN_FIELDS.lastCommentAuthor
    && field !== ALARM_PATTERN_FIELDS.lastCommentInitiator
    && field !== ALARM_PATTERN_FIELDS.stateInitiator
    && field !== ENTITY_PATTERN_FIELDS.id
    && field !== ENTITY_PATTERN_FIELDS.component
    && field !== ENTITY_PATTERN_FIELDS.connector
    && field !== ENTITY_PATTERN_FIELDS.category
    && field !== EVENT_FILTER_PATTERN_FIELDS.eventType
    && field !== EVENT_FILTER_PATTERN_FIELDS.component
    && field !== EVENT_FILTER_PATTERN_FIELDS.connector
    && field !== EVENT_FILTER_PATTERN_FIELDS.connectorName
    && field !== EVENT_FILTER_PATTERN_FIELDS.resource
    && field !== EVENT_FILTER_PATTERN_FIELDS.initiator
    && field !== EVENT_FILTER_PATTERN_FIELDS.author
);

/**
 * Check pattern rule is valid
 *
 * @param {PatternRule | *} rule
 * @return {boolean}
 */
export const isValidPatternRule = rule => !!(rule?.field || rule?.alias)
  && !isNil(rule.cond?.value)
  && !isNil(rule.cond?.type)
  && (!rule.field_type || isValidRuleFieldType(rule.field_type))
  && isValidPatternCondition(rule.cond.type)
  && isValidRuleValue(rule);

/**
 * Convert any value to type value
 *
 * @param {PatternFieldType} type
 * @param {PatternValue} [value]
 * @param [defaultValue]
 * @return {PatternValue | undefined}
 */
export const convertValueByType = (value, type, defaultValue) => {
  if (isEmpty(value) && !isUndefined(defaultValue)) {
    return defaultValue;
  }

  const preparedValue = isArray(value) ? value[0] : value;

  switch (type) {
    case PATTERN_FIELD_TYPES.number:
      return Number(preparedValue) || 0;
    case PATTERN_FIELD_TYPES.boolean:
      return Boolean(preparedValue);
    case PATTERN_FIELD_TYPES.string:
      return (isNaN(preparedValue) || isNull(preparedValue) || isUndefined(preparedValue))
        ? ''
        : String(preparedValue);
    case PATTERN_FIELD_TYPES.null:
      return null;
    case PATTERN_FIELD_TYPES.stringArray:
      return preparedValue ? primitiveArrayToForm([String(preparedValue)]) : [];
    default:
      return undefined;
  }
};

/**
 * Get operators by type of value
 *
 * @param {PatternFieldType} fieldType
 * @return {string[]}
 */
export const getOperatorsByFieldType = (fieldType) => {
  switch (fieldType) {
    case PATTERN_FIELD_TYPES.number:
      return PATTERN_NUMBER_OPERATORS;

    case PATTERN_FIELD_TYPES.stringArray:
      return [
        PATTERN_OPERATORS.hasEvery,
        PATTERN_OPERATORS.hasOneOf,
        PATTERN_OPERATORS.hasNot,
        PATTERN_OPERATORS.isEmpty,
        PATTERN_OPERATORS.isNotEmpty,
      ];

    case PATTERN_FIELD_TYPES.boolean:
      return [PATTERN_OPERATORS.equal];

    case PATTERN_FIELD_TYPES.timestamp:
      return PATTERN_DATE_OPERATORS;

    default:
      return PATTERN_STRING_OPERATORS;
  }
};

/**
 * Get operators by rule type and rule values
 *
 * @param {PatternRuleForm} rule
 * @param {PatternRuleType} ruleType
 * @return {string[]}
 */
export const getOperatorsByRule = (rule, ruleType) => {
  if (isDurationRuleType(ruleType)) {
    return PATTERN_DURATION_OPERATORS;
  }

  const isAnyInfosType = isInfosRuleType(ruleType) || isExtraInfosRuleType(ruleType);

  if (isAnyInfosType && rule.field === PATTERN_RULE_INFOS_FIELDS.name) {
    return PATTERN_EXISTS_OPERATORS;
  }

  let operators = getOperatorsByFieldType(rule.fieldType);

  if (rule.fieldType === PATTERN_FIELD_TYPES.string || isObjectRuleType(ruleType)) {
    operators = [
      ...operators,
      PATTERN_OPERATORS.isOneOf,
      PATTERN_OPERATORS.isNotOneOf,
    ];
  }

  if (isDateRuleType(ruleType)) {
    operators = PATTERN_DATE_OPERATORS;
  }

  return operators;
};

/**
 * Get value type by operator
 *
 * @param {string} operator
 * @return {PatternFieldType[]}
 */
export const getValueTypesByOperator = (operator) => {
  const operators = [];

  if (isOperatorForArray(operator)) {
    operators.push(PATTERN_FIELD_TYPES.stringArray);
  }

  if (isOperatorForString(operator)) {
    operators.push(PATTERN_FIELD_TYPES.string);
  }

  if (isOperatorForNumber(operator)) {
    operators.push(PATTERN_FIELD_TYPES.number);
  }

  if (isOperatorForBoolean(operator)) {
    operators.push(PATTERN_FIELD_TYPES.boolean);
  }

  if (isOperatorForNull(operator)) {
    operators.push(PATTERN_FIELD_TYPES.null);
  }

  return operators;
};

/**
 * Convert value to operator type
 *
 * @param {PatternValue} value
 * @param {string} operator
 * @return {PatternValue|undefined|*}
 */
export const convertValueByOperator = (value, operator) => {
  const fieldType = getFieldType(value);

  const operatorsValueType = getValueTypesByOperator(operator);

  if (operatorsValueType.includes(fieldType)) {
    return value;
  }

  return convertValueByType(value, operatorsValueType[0]);
};

/**
 * Create id pattern rule for entity patterns
 *
 * @param {string | string[]} value
 * @returns {PatternGroups}
 */
export const createEntityIdPatternByValue = value => [[{
  field: ENTITY_PATTERN_FIELDS.id,
  cond: {
    type: isArray(value) ? PATTERN_CONDITIONS.isOneOf : PATTERN_CONDITIONS.equal,
    value,
  },
}]];

/**
 * Get default date form range configuration
 *
 * @return {Object} Default range object with type, from, and to properties
 * @return {string} type - Quick range type (default: last1Hour.value)
 * @return {number} from - Start timestamp (default: 0)
 * @return {number} to - End timestamp (default: 0)
 */
export const getDefaultDateFormRange = () => ({
  type: QUICK_RANGES.last1Hour.value,
  from: 0,
  to: 0,
});

/**
 * Get quick range key by duration object
 *
 * @param {Duration} duration - Duration object with value and unit
 * @return {string} Quick range key or custom value if no match found
 */
export const getPatternQuickByDuration = (duration) => {
  const [quickRangeValue] = Object.entries(PATTERN_QUICK_RANGES_DURATIONS).find(([, value]) => isEqual(value, duration))
   ?? [];

  return quickRangeValue ?? QUICK_RANGES.custom.value;
};

/**
 * Convert pattern rule with date conditions to form format
 *
 * @param {PatternRule} [rule={}] - Pattern rule with date condition
 * @param {Object} [rule.cond] - Rule condition object
 * @param {string} [rule.cond.type] - Condition type (absoluteTime, relativeTime)
 * @param {Object|Duration} [rule.cond.value] - Condition value
 * @param {number} [rule.cond.value.from] - Start timestamp for absoluteTime
 * @param {number} [rule.cond.value.to] - End timestamp for absoluteTime
 * @param {Duration} [rule.cond.value.from] - Start duration for relativeTime range
 * @param {Duration} [rule.cond.value.to] - End duration for relativeTime range
 * @param {number} [rule.cond.value.value] - Duration value for relativeTime
 * @param {string} [rule.cond.value.unit] - Duration unit for relativeTime
 * @return {Object} Form object with operator and range
 * @return {string} operator - Pattern operator (inRangeDates, inRangePeriod, within, olderThan)
 * @return {Object} range - Range configuration object
 * @return {string} [range.type] - Quick range type
 * @return {number|Date} [range.from] - Range start value
 * @return {number|Date} [range.to] - Range end value
 */
export const patternDateRangeToForm = (rule = {}) => {
  const range = getDefaultDateFormRange();

  if (rule.cond.type === PATTERN_CONDITIONS.absoluteTime) {
    range.from = convertDateToDateObject(rule.cond.value?.from);
    range.to = convertDateToDateObject(rule.cond.value?.to);

    return {
      operator: PATTERN_OPERATORS.inRangeDates,
      range,
    };
  }

  if (rule.cond.type === PATTERN_CONDITIONS.relativeTime) {
    if (rule.cond.value?.from && rule.cond.value?.to) {
      range.from = getPatternQuickByDuration(rule.cond.value?.from);
      range.to = getPatternQuickByDuration(rule.cond.value?.to);

      if (range.from === QUICK_RANGES.custom.value) {
        range.fromCustom = rule.cond.value?.from;
      }

      if (range.to === QUICK_RANGES.custom.value) {
        range.toCustom = rule.cond.value?.to;
      }

      return {
        operator: PATTERN_OPERATORS.inRangePeriod,
        range,
      };
    } if (rule.cond.value?.to) {
      range.type = getPatternQuickByDuration(rule.cond.value?.to);

      if (range.type === QUICK_RANGES.custom.value) {
        range.typeCustom = rule.cond.value?.to;
      }

      return {
        operator: PATTERN_OPERATORS.olderThan,
        range,
      };
    }

    range.type = getPatternQuickByDuration(rule.cond.value);

    if (range.type === QUICK_RANGES.custom.value) {
      range.typeCustom = rule.cond.value;
    }

    return {
      operator: PATTERN_OPERATORS.within,
      range,
    };
  }

  return {
    operator: PATTERN_OPERATORS.olderThan,
    range,
  };
};

/**
 * Convert pattern rule to form
 *
 * @param {PatternRule} rule
 * @return {PatternRuleForm}
 */
export const patternRuleToForm = (rule = {}) => {
  const form = {
    key: uid(),
    attribute: rule.alias || rule.field || '',
    operator: '',
    field: '',
    fieldType: rule.field_type ?? PATTERN_FIELD_TYPES.string,
    dictionary: '',
    value: '',
    alias: !!rule.alias,
    range: {
      type: QUICK_RANGES.last1Hour.value,
      from: 0,
      to: 0,
    },
    duration: durationToForm(),
  };

  if (!rule.cond) {
    return form;
  }

  const isAlarmInfos = rule.field?.startsWith(ALARM_PATTERN_FIELDS.infos);
  const isEntityInfos = rule.field?.startsWith(ENTITY_PATTERN_FIELDS.infos);
  const isEntityComponentInfos = rule.field?.startsWith(ENTITY_PATTERN_FIELDS.componentInfos);
  const isDuration = rule.field === ALARM_PATTERN_FIELDS.duration;
  const isInfos = isAlarmInfos || isEntityInfos || isEntityComponentInfos;
  const isExtraInfos = !isInfos && rule.field?.startsWith(EVENT_FILTER_PATTERN_FIELDS.extraInfos);
  const patternObjectField = getObjectPatternRuleField(rule.field);

  switch (rule.cond.type) {
    case PATTERN_CONDITIONS.equal: {
      if (isBoolean(rule.cond.value)) {
        if (rule.field === SERVICE_WEATHER_PATTERN_FIELDS.grey) {
          form.operator = rule.cond.value ? PATTERN_OPERATORS.isGrey : PATTERN_OPERATORS.isNotGrey;
        } else if (rule.field === ALARM_PATTERN_FIELDS.meta) {
          form.operator = PATTERN_OPERATORS.ruleIs;
        } else if (rule.field === ENTITY_PATTERN_FIELDS.enabled) {
          form.operator = rule.cond.value ? PATTERN_OPERATORS.enabled : PATTERN_OPERATORS.disabled;
        }
      }

      if (!form.operator) {
        form.operator = {
          [ALARM_PATTERN_FIELDS.meta]: PATTERN_OPERATORS.ruleIs,
        }[rule.field] ?? PATTERN_OPERATORS.equal;
        form.value = rule.cond.value;
      }
      break;
    }
    case PATTERN_CONDITIONS.notEqual:
      form.operator = PATTERN_OPERATORS.notEqual;
      form.value = rule.cond.value;
      break;

    case PATTERN_CONDITIONS.greater:
      form.operator = isDuration
        ? PATTERN_OPERATORS.longer
        : PATTERN_OPERATORS.higher;
      form.value = isDuration ? '' : rule.cond.value;
      break;
    case PATTERN_CONDITIONS.less:
      form.operator = isDuration
        ? PATTERN_OPERATORS.shorter
        : PATTERN_OPERATORS.lower;
      form.value = isDuration ? '' : rule.cond.value;
      break;

    case PATTERN_CONDITIONS.exist:
      if (rule.cond.value) {
        form.operator = {
          [ALARM_PATTERN_FIELDS.snooze]: PATTERN_OPERATORS.snoozed,
          [ALARM_PATTERN_FIELDS.ack]: PATTERN_OPERATORS.acked,
          [ALARM_PATTERN_FIELDS.canceled]: PATTERN_OPERATORS.canceled,
          [ALARM_PATTERN_FIELDS.ticket]: PATTERN_OPERATORS.ticketAssociated,
          [ALARM_PATTERN_FIELDS.activationDate]: PATTERN_OPERATORS.activated,
          [ALARM_PATTERN_FIELDS.meta]: PATTERN_OPERATORS.isMetaAlarm,
        }[rule.field];
      } else {
        form.operator = {
          [ALARM_PATTERN_FIELDS.snooze]: PATTERN_OPERATORS.notSnoozed,
          [ALARM_PATTERN_FIELDS.ack]: PATTERN_OPERATORS.notAcked,
          [ALARM_PATTERN_FIELDS.canceled]: PATTERN_OPERATORS.notCanceled,
          [ALARM_PATTERN_FIELDS.ticket]: PATTERN_OPERATORS.ticketNotAssociated,
          [ALARM_PATTERN_FIELDS.activationDate]: PATTERN_OPERATORS.inactive,
          [ALARM_PATTERN_FIELDS.meta]: PATTERN_OPERATORS.isNotMetaAlarm,
        }[rule.field];
      }

      if (!form.operator) {
        form.operator = rule.cond.value === true
          ? PATTERN_OPERATORS.exist
          : PATTERN_OPERATORS.notExist;
      }

      if (rule.field === ALARM_PATTERN_FIELDS.activationDate) {
        form.attribute = ALARM_PATTERN_FIELDS.activated;
      }
      break;

    case PATTERN_CONDITIONS.hasEvery:
      form.operator = PATTERN_OPERATORS.hasEvery;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.isOneOf:
      form.operator = PATTERN_OPERATORS.isOneOf;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.hasOneOf:
      form.operator = rule.field === ALARM_PATTERN_FIELDS.tags
        ? PATTERN_OPERATORS.with
        : PATTERN_OPERATORS.hasOneOf;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.hasNot:
      form.operator = rule.field === ALARM_PATTERN_FIELDS.tags
        ? PATTERN_OPERATORS.without
        : PATTERN_OPERATORS.hasNot;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.isNotOneOf:
      form.operator = PATTERN_OPERATORS.isNotOneOf;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.isEmpty:
      form.operator = rule.cond.value === true
        ? PATTERN_OPERATORS.isEmpty
        : PATTERN_OPERATORS.isNotEmpty;
      form.value = [];
      break;

    case PATTERN_CONDITIONS.contains:
      form.operator = PATTERN_OPERATORS.contains;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.notContains:
      form.operator = PATTERN_OPERATORS.notContains;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.beginWith:
      form.operator = PATTERN_OPERATORS.beginWith;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.notBeginWith:
      form.operator = PATTERN_OPERATORS.notBeginWith;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.endsWith:
      form.operator = PATTERN_OPERATORS.endsWith;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.notEndWith:
      form.operator = PATTERN_OPERATORS.notEndWith;
      form.value = rule.cond.value;
      break;
    case PATTERN_CONDITIONS.regexp:
      form.operator = PATTERN_OPERATORS.regexp;
      form.value = rule.cond.value;
      break;

    case PATTERN_CONDITIONS.relativeTime:
    case PATTERN_CONDITIONS.absoluteTime: {
      const { operator, range } = patternDateRangeToForm(rule);

      form.operator = operator;
      form.range = range;
      break;
    }

    case PATTERN_CONDITIONS.hasLabels:
      form.operator = PATTERN_OPERATORS.withLabel;
      form.value = rule.cond.value;
      break;

    case PATTERN_CONDITIONS.hasNotLabels:
      form.operator = PATTERN_OPERATORS.withoutLabel;
      form.value = rule.cond.value;
      break;
  }

  if (isDuration) {
    form.duration = durationToForm(rule.cond.value);
  }

  if ((isExtraInfos || isInfos) && !rule.alias) {
    if (isExtraInfos) {
      form.attribute = EVENT_FILTER_PATTERN_FIELDS.extraInfos;
      form.dictionary = rule.field.slice(EVENT_FILTER_PATTERN_FIELDS.extraInfos.length + 1);
    } else if (isInfos) {
      if (isAlarmInfos) {
        form.attribute = ALARM_PATTERN_FIELDS.infos;
      } else if (isEntityInfos) {
        form.attribute = ENTITY_PATTERN_FIELDS.infos;
      } else {
        form.attribute = ENTITY_PATTERN_FIELDS.componentInfos;
      }

      form.dictionary = rule.field.slice(form.attribute.length + 1);
    }

    form.field = PATTERN_EXISTS_OPERATORS.includes(rule.cond.type)
      ? PATTERN_RULE_INFOS_FIELDS.name
      : PATTERN_RULE_INFOS_FIELDS.value;
  }

  if (patternObjectField) {
    form.attribute = patternObjectField;
    form.dictionary = rule.field.replace(`${patternObjectField}.`, '');
  }

  if (
    (form.fieldType === PATTERN_FIELD_TYPES.stringArray || isArrayOperator(form.operator))
    && isArray(form.value)
    && (!form.value.length || !form.value[0]?.key)
    && !isArrayPatternRuleFieldWithoutPrimitive(form.field)
  ) {
    form.value = primitiveArrayToForm(form.value);
  }

  return form;
};

/**
 * Convert pattern rules to form
 *
 * @param {PatternRules} [rules = []]
 * @return {PatternRuleForm[]}
 */
export const patternRulesToForm = (rules = []) => rules.map(patternRuleToForm);

/**
 * Convert pattern rules to group form
 *
 * @param {PatternRules} [rules = []]
 * @return {PatternGroupForm}
 */
export const patternRulesToGroup = rules => ({
  key: uid(),
  rules: patternRulesToForm(rules),
});

/**
 * Convert patterns array to groups format
 *
 * @param {PatternRules[]} [patterns = []] - Array of pattern rules
 * @return {PatternGroupForm[]} Array of pattern group forms
 */
export const patternsToGroups = (patterns = []) => patterns.map(patternRulesToGroup);

/**
 * Convert pattern to pattern form
 *
 * @param {Pattern} pattern
 * @return {PatternForm}
 */
export const patternToForm = (pattern = {}) => ({
  ...omit(pattern, [PATTERNS_FIELDS.alarm, PATTERNS_FIELDS.entity, PATTERNS_FIELDS.pbehavior, PATTERNS_FIELDS.event]),

  title: pattern.title ?? '',
  id: pattern.id ?? PATTERN_CUSTOM_ITEM_VALUE,
  type: pattern.type ?? PATTERN_TYPES.alarm,
  is_corporate: pattern.is_corporate ?? false,
  groups: patternsToGroups(
    pattern.alarm_pattern
    || pattern.entity_pattern
    || pattern.pbehavior_pattern
    || pattern.event_pattern
    || pattern.total_entity_pattern
    || pattern.weather_service_pattern,
  ),
});

/**
 * Returns pattern field keys (e.g. `alarm_pattern`) whose blocks differ between two forms that carry
 * the same pattern-* shape as filter forms.
 * Each block is compared with `isOmitEqual`, omitting `id` for the check.
 *
 * @param {Object} [newForm={}]
 * @param {Object} [oldForm={}]
 * @param {PatternsFields} [fields=Object.values(PATTERNS_FIELDS)]
 * @returns {string[]}
 */
export const getChangedPatternsFields = (
  newForm = {},
  oldForm = {},
  fields = Object.values(PATTERNS_FIELDS),
) => fields.filter(fieldKey => !isEqual(newForm?.[fieldKey] ?? [], oldForm?.[fieldKey] ?? []));

/**
 * Get duration from rule range by specified keys
 *
 * @param {PatternRuleForm} rule - Pattern rule form object
 * @param {string} [valueKey = 'type'] - Key to get duration type from range
 * @param {string} [customKey = 'typeCustom'] - Key to get custom duration from range
 * @return {Duration} Duration object from pattern quick ranges or custom duration
 */
export const getRuleRangeDuration = (rule, valueKey = 'type', customKey = 'typeCustom') => {
  const { range } = rule;

  if (range[valueKey] === QUICK_RANGES.custom.value) {
    return range[customKey];
  }

  return PATTERN_QUICK_RANGES_DURATIONS[range[valueKey]] ?? {};
};

/**
 * Convert range to pattern condition
 *
 * @param {PatternRuleRangeForm} range
 * @return {PatternRuleCondition}
 */
export const formDateIntervalConditionToPatternRuleCondition = (rule) => {
  const { operator, range } = rule;

  if (operator === PATTERN_OPERATORS.inRangeDates) {
    return {
      type: PATTERN_CONDITIONS.absoluteTime,
      value: {
        from: convertDateToTimestamp(range.from),
        to: convertDateToTimestamp(range.to),
      },
    };
  }

  if (operator === PATTERN_OPERATORS.inRangePeriod) {
    return {
      type: PATTERN_CONDITIONS.relativeTime,
      value: {
        from: getRuleRangeDuration(rule, 'from', 'fromCustom'),
        to: getRuleRangeDuration(rule, 'to', 'toCustom'),
      },
    };
  }

  if (operator === PATTERN_OPERATORS.within) {
    return {
      type: PATTERN_CONDITIONS.relativeTime,
      value: getRuleRangeDuration(rule),
    };
  }

  return {
    type: PATTERN_CONDITIONS.relativeTime,
    value: {
      to: getRuleRangeDuration(rule),
    },
  };
};

/**
 * Convert pattern form rule to pattern rule
 *
 * @param {PatternRuleForm} rule
 * @return {PatternRule}
 */
export const formRuleToPatternRule = (rule) => {
  const pattern = {
    [rule.alias ? 'alias' : 'field']: rule.attribute === ALARM_PATTERN_FIELDS.activated
      ? ALARM_PATTERN_FIELDS.activationDate
      : rule.attribute,
    cond: {
      value: rule.value,
      type: PATTERN_CONDITIONS.equal,
    },
  };

  const isInfos = isInfosPatternRuleField(rule.attribute);
  const isExtraInfos = isExtraInfosPatternRuleField(rule.attribute);
  const isDate = isDatePatternRuleField(rule.attribute);
  const isObject = isObjectPatternRuleField(rule.attribute);

  if (isInfos || isExtraInfos || isObject) {
    pattern.field = rule.dictionary ? [rule.attribute, rule.dictionary].join('.') : rule.attribute;
  }

  if ((rule.alias || isExtraInfos || isInfos) && rule.field !== PATTERN_RULE_INFOS_FIELDS.name) {
    pattern.field_type = rule.fieldType;
  }

  if (isDate || rule.fieldType === PATTERN_FIELD_TYPES.timestamp) {
    pattern.cond = formDateIntervalConditionToPatternRuleCondition(rule);

    return pattern;
  }

  if ((isExtraInfos || isInfos) && rule.field !== PATTERN_RULE_INFOS_FIELDS.name) {
    pattern.field_type = rule.fieldType;
  }

  if (
    (rule.fieldType === PATTERN_FIELD_TYPES.stringArray || isArrayOperator(rule.operator))
    && isArray(pattern.cond.value)
  ) {
    pattern.cond.value = formToPrimitiveArray(pattern.cond.value);
  }

  switch (rule.operator) {
    case PATTERN_OPERATORS.equal:
    case PATTERN_OPERATORS.ruleIs:
      pattern.cond.type = PATTERN_CONDITIONS.equal;
      break;
    case PATTERN_OPERATORS.notEqual:
      pattern.cond.type = PATTERN_CONDITIONS.notEqual;
      break;
    case PATTERN_OPERATORS.contains:
      pattern.cond.type = PATTERN_CONDITIONS.contains;
      break;
    case PATTERN_OPERATORS.notContains:
      pattern.cond.type = PATTERN_CONDITIONS.notContains;
      break;

    case PATTERN_OPERATORS.beginWith:
      pattern.cond.type = PATTERN_CONDITIONS.beginWith;
      break;
    case PATTERN_OPERATORS.notBeginWith:
      pattern.cond.type = PATTERN_CONDITIONS.notBeginWith;
      break;
    case PATTERN_OPERATORS.endsWith:
      pattern.cond.type = PATTERN_CONDITIONS.endsWith;
      break;
    case PATTERN_OPERATORS.notEndWith:
      pattern.cond.type = PATTERN_CONDITIONS.notEndWith;
      break;
    case PATTERN_OPERATORS.regexp:
      pattern.cond.type = PATTERN_CONDITIONS.regexp;
      break;

    case PATTERN_OPERATORS.hasEvery:
      pattern.cond.type = PATTERN_CONDITIONS.hasEvery;
      break;
    case PATTERN_OPERATORS.isOneOf:
      pattern.cond.type = PATTERN_CONDITIONS.isOneOf;
      break;
    case PATTERN_OPERATORS.hasOneOf:
      pattern.cond.type = PATTERN_CONDITIONS.hasOneOf;
      break;
    case PATTERN_OPERATORS.hasNot:
      pattern.cond.type = PATTERN_CONDITIONS.hasNot;
      break;
    case PATTERN_OPERATORS.isNotOneOf:
      pattern.cond.type = PATTERN_CONDITIONS.isNotOneOf;
      break;
    case PATTERN_OPERATORS.isEmpty:
      pattern.cond.type = PATTERN_CONDITIONS.isEmpty;
      pattern.cond.value = true;
      break;
    case PATTERN_OPERATORS.isNotEmpty:
      pattern.cond.type = PATTERN_CONDITIONS.isEmpty;
      pattern.cond.value = false;
      break;

    case PATTERN_OPERATORS.higher:
      pattern.cond.type = PATTERN_CONDITIONS.greater;
      pattern.cond.value = Number(pattern.cond.value);
      break;

    case PATTERN_OPERATORS.lower:
      pattern.cond.type = PATTERN_CONDITIONS.less;
      pattern.cond.value = Number(pattern.cond.value);
      break;

    case PATTERN_OPERATORS.longer:
      pattern.cond.type = PATTERN_CONDITIONS.greater;
      pattern.cond.value = rule.duration;
      break;

    case PATTERN_OPERATORS.shorter:
      pattern.cond.type = PATTERN_CONDITIONS.less;
      pattern.cond.value = rule.duration;
      break;

    case PATTERN_OPERATORS.isGrey:
      pattern.cond.type = PATTERN_CONDITIONS.equal;
      pattern.cond.value = true;
      break;
    case PATTERN_OPERATORS.isNotGrey:
      pattern.cond.type = PATTERN_CONDITIONS.equal;
      pattern.cond.value = false;
      break;

    case PATTERN_OPERATORS.exist:
    case PATTERN_OPERATORS.ticketAssociated:
    case PATTERN_OPERATORS.canceled:
    case PATTERN_OPERATORS.snoozed:
    case PATTERN_OPERATORS.acked:
    case PATTERN_OPERATORS.activated:
    case PATTERN_OPERATORS.isMetaAlarm:
      pattern.cond.type = PATTERN_CONDITIONS.exist;
      pattern.cond.value = true;
      break;
    case PATTERN_OPERATORS.notExist:
    case PATTERN_OPERATORS.ticketNotAssociated:
    case PATTERN_OPERATORS.notCanceled:
    case PATTERN_OPERATORS.notSnoozed:
    case PATTERN_OPERATORS.notAcked:
    case PATTERN_OPERATORS.inactive:
    case PATTERN_OPERATORS.isNotMetaAlarm:
      pattern.cond.type = PATTERN_CONDITIONS.exist;
      pattern.cond.value = false;
      break;
    case PATTERN_OPERATORS.with:
      pattern.cond.type = PATTERN_CONDITIONS.hasOneOf;
      break;
    case PATTERN_OPERATORS.without:
      pattern.cond.type = PATTERN_CONDITIONS.hasNot;
      break;
    case PATTERN_OPERATORS.withLabel:
      pattern.cond.type = PATTERN_CONDITIONS.hasLabels;
      break;
    case PATTERN_OPERATORS.withoutLabel:
      pattern.cond.type = PATTERN_CONDITIONS.hasNotLabels;
      break;
    case PATTERN_OPERATORS.enabled:
    case PATTERN_OPERATORS.disabled:
      pattern.cond.type = PATTERN_CONDITIONS.equal;
      pattern.cond.value = rule.operator === PATTERN_OPERATORS.enabled;
      break;
  }

  return pattern;
};

/**
 * Convert form group to pattern rules
 *
 * @param {PatternGroupForm} group
 * @return {PatternRules}
 */
export const formGroupToPatternRules = group => group.rules.map(formRuleToPatternRule);

/**
 * Convert form groups to pattern rules
 *
 * @param {PatternGroupsForm} groups
 * @return {PatternGroups}
 */
export const formGroupsToPatternRules = groups => groups.map(formGroupToPatternRules);

/**
 * Convert form groups to pattern rules query
 *
 * @param {PatternGroupsForm} [groups = []]
 * @return {string}
 */
export const formGroupsToPatternRulesQuery = (groups = []) => JSON.stringify(formGroupsToPatternRules(groups));

/**
 * Convert pattern form to pattern
 *
 * @param {PatternForm} form
 * @return {Pattern}
 */
export const formToPattern = (form) => {
  const { groups, id, ...pattern } = form;

  const patternsKey = {
    [PATTERN_TYPES.alarm]: PATTERNS_FIELDS.alarm,
    [PATTERN_TYPES.entity]: PATTERNS_FIELDS.entity,
    [PATTERN_TYPES.pbehavior]: PATTERNS_FIELDS.pbehavior,
    [PATTERN_TYPES.serviceWeather]: PATTERNS_FIELDS.serviceWeather,
  }[form.type];

  pattern[patternsKey] = formGroupsToPatternRules(groups);

  return pattern;
};
